import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { CombinedState } from 'reducers';
import { getCore } from 'cvat-core-wrapper';
import { isReferenceJob } from 'utils/reference-job';
import { prefetchJob } from 'utils/job-prefetch';
import ModeSwitch, { WorkspaceMode } from './mode-switch';
import { useTaskSummary } from './use-task-summary';
import {
    rememberTaskJob, switchTarget, SwitchTarget, useTaskModes,
} from './references/use-task-modes';
import { useSaveThenNavigate } from './references/use-save-then-navigate';

// cvat-core caches frames by bare job id: a task and a reference job sharing it would mix images
function prefetch(target: SwitchTarget, warm: boolean): void {
    const core = getCore();
    const load = target.kind === 'task' ?
        async () => (await core.jobs.get({ jobID: target.jobId }))[0] :
        async () => (await core.reference_jobs.get({ jobID: target.jobId }))[0];
    // cvat-core keeps decoded frames per job: the switch then paints without waiting for the image
    prefetchJob(target.kind, target.jobId, load, warm ? async (job) => {
        const frame = await job.frames.get(target.frame ?? job.startFrame);
        await frame.data();
    } : undefined);
}

const whenIdle = (callback: () => void): (() => void) => {
    if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(callback, { timeout: 3000 });
        return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(callback, 1500);
    return () => window.clearTimeout(handle);
};

export default function ModeSwitchContainer({ taskId }: { taskId: number }): JSX.Element {
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const fetching = useSelector((state: CombinedState) => state.annotation.job.fetching);
    const saving = useSelector((state: CombinedState) => state.annotation.annotations.saving.uploading);
    const modes = useTaskModes(taskId);
    const { size } = useTaskSummary(taskId);
    const navigate = useSaveThenNavigate();
    const [busy, setBusy] = useState(false);
    const mounted = useRef(true);
    const { rid } = useParams<{ rid?: string }>();
    // the route flips at once; the loaded job follows a moment later
    const mode: WorkspaceMode = rid === undefined ? 'drawings' : 'references';
    const other: WorkspaceMode = mode === 'drawings' ? 'references' : 'drawings';

    useEffect(() => () => { mounted.current = false; }, []);

    useEffect(() => {
        if (job && !isReferenceJob(job)) rememberTaskJob(taskId, job.id, frame);
    }, [job?.id, frame, taskId]);

    const prefetchOther = useCallback(() => {
        const target = modes && switchTarget(other, modes, taskId);
        if (target) prefetch(target, target.jobId !== job?.id);
    }, [modes, other, taskId, job?.id]);

    // after the open job has loaded, so the prefetch never competes with it
    useEffect(() => (job && !fetching ? whenIdle(prefetchOther) : undefined), [job?.id, fetching, prefetchOther]);

    const onSwitch = async (next: WorkspaceMode): Promise<void> => {
        if (!modes || next === mode) return;
        const target = switchTarget(next, modes, taskId);
        if (!target) return;
        setBusy(true);
        try {
            await navigate(target.path);
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    return (
        <ModeSwitch
            mode={mode}
            drawings={size}
            references={modes ? modes.photos.length : null}
            // a second switch mid-load would show the abandoned job for a moment
            busy={busy || saving || fetching}
            onSwitch={onSwitch}
            onIntent={prefetchOther}
        />
    );
}
